import { useQuery } from '@tanstack/react-query';
import { WithQueryStatus } from '../../../types/withQueryStatus';
import { ILeadLegalInfo } from '@clickersphere/core/entities/ILeadLegalInfo';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import LeadLegalInfoService from '@clickersphere/core/services/LeadLegalInfoService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { useUserMe } from '../user/useUserMe';

export const LEAD_LEGAL_INFO_QUERY_KEY = 'lead-legal-info';

export type LeadLegalInfoQueryData = WithQueryStatus<ILeadLegalInfo>;

const LEAD_LEGAL_INFO_NOT_FOUND_CODE = 'LEADLEGAL-0001';

export function useLeadLegalInfo(leadId: string) {
  const { selectedTenantId } = useUserMe();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    data: leadLegalInfo,
    isSuccess: isLeadLegalInfoSuccess,
    isFetching: isLeadLegalInfoFetching,
    isLoading: isLeadLegalInfoLoading,
  } = useQuery({
    staleTime: 1000 * 60 * 5,
    enabled: !!selectedTenantId && !!leadId,
    queryKey: buildQueryKey([LEAD_LEGAL_INFO_QUERY_KEY, leadId]),
    queryFn: async (): Promise<LeadLegalInfoQueryData | null> => {
      try {
        const result = await LeadLegalInfoService.getByLeadId(leadId);
        return result as LeadLegalInfoQueryData;
      } catch (error) {
        if (
          error instanceof ResponseError &&
          error.code === LEAD_LEGAL_INFO_NOT_FOUND_CODE
        ) {
          return null;
        }
        throw error;
      }
    },
  });

  return {
    leadLegalInfo: leadLegalInfo ?? null,
    isLeadLegalInfoSuccess,
    isLeadLegalInfoFetching,
    isLeadLegalInfoLoading,
  };
}
